// ─── Con qué cara llega cada aviso a la campana ──────────────────────────────
//
// Vive fuera de la pantalla —y no dentro de `pages/Notificaciones.tsx`— para que se
// pueda PROBAR sin montar media aplicación. La regla de negocio «la mora se ve en
// rojo» no debería depender de que alguien abra el navegador para comprobarla.
//
// 📜 Decisión de Mau (2026-08-17): **la mora se le muestra a cobranza en ROJO.**
// Revoca la propuesta anterior de la pieza, que la dejaba en ámbar (`urgente`) — ver
// `qa/ESTADO-I105-2026-08-14.md` §D10, que la marcó explícitamente «queda a tu
// revocación». Esto es esa revocación.

import { NOTIFICACION_TIPO_COBRANZA } from '@erp/shared';

export type Urgencia = 'critica' | 'urgente' | 'info';

/**
 * Rojo. Lo que cuesta dinero mientras nadie lo mira.
 *
 * ⚠️ Los seis primeros son nombres de eventos del canal de CORREO
 * (`notificaciones.service.ts`, `DEFAULT_PREFERENCIAS_POR_ROL`), no tipos de aviso:
 * MEDIDO el 2026-08-17, **ninguno de los seis está declarado en `NOTIFICACION_TIPO`
 * ni lo emite un solo punto del código**, así que hasta hoy esta lista no pintaba de
 * rojo absolutamente nada. Se dejan —quitarlos es de la pieza que unifique los tres
 * vocabularios (campana · correo · catálogo), y borrarlos escondería ese hueco— pero
 * queda dicho aquí para que nadie los lea como «esto ya funciona».
 */
export const TIPOS_CRITICOS = new Set<string>([
  'PAGO_AUTORIZADO_CON_BYPASS', 'CREDITO_VENCIDO', 'TICKET_SLA_VENCIDO',
  'SALDO_CRITICO', 'CIERRE_MES_PROPUESTO', 'BYPASS_AUTORIZADO',
  // 🔴 La mora, en rojo. Es dinero que se está perdiendo todos los días, y es el
  // primer tipo de esta lista que el sistema emite de verdad.
  NOTIFICACION_TIPO_COBRANZA.INGRESO_MORA_ALERTA,
]);

/**
 * Ámbar. Importa hoy, pero no está costando dinero por hora.
 *
 * El pago recibido NO entra aquí a propósito: es buena noticia y como `info` cumple.
 * Si todo fuera urgente, nada lo sería.
 */
export const TIPOS_URGENTES = new Set<string>([
  'TAREA_ASIGNADA', 'TAREA_MENCION', 'CHAT_MENCION', 'PAGO_VENCIDO',
  'TICKET_CREADO', 'ACTA_FIRMADA', 'CONTRATO_FIRMADO',
  // 🟠 I-116: se canceló un cobro y la cuota volvió a deberse. En `info` se pintaba
  // VERDE, del mismo color que «pago recibido» — la noticia contraria con la cara de la
  // buena. No es `critica` porque el dinero todavía no se está perdiendo por hora (para
  // eso está la mora), pero cobranza tiene que volver a cobrarla: es acción pendiente.
  NOTIFICACION_TIPO_COBRANZA.COBRO_CANCELADO,
]);

export function getUrgencia(tipo: string): Urgencia {
  if (TIPOS_CRITICOS.has(tipo)) return 'critica';
  if (TIPOS_URGENTES.has(tipo)) return 'urgente';
  return 'info';
}

export const URG_COLOR: Record<Urgencia, string> = {
  critica: '#EF4444',   // rojo
  urgente: '#F59E0B',   // ámbar
  info:    '#22C55E',   // verde
};

export const URG_BG: Record<Urgencia, string> = {
  critica: '#FEF2F2',
  urgente: '#FFFBEB',
  info:    'transparent',
};
